import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import PageBanner from "@/components/PageBanner";
import {
  Leaf,
  Heart,
  Users,
  Recycle,
  LightbulbIcon,
  Plane,
  Building,
  Map,
} from "lucide-react";
import { Link } from "wouter";

const sections = [
  {
    title: "Environmental Conservation",
    icon: Leaf,
    points: [
      "Reduce, Reuse, And Recycle: We encourage our travelers to reduce their waste, reuse items when possible, and recycle whenever facilities are available.",
      "Minimize Carbon Footprint: We strive to minimize our carbon footprint by using fuel-efficient vehicles, optimizing travel routes, and promoting eco-friendly accommodations.",
      "Protect Local Wildlife: We support conservation efforts and promote responsible wildlife viewing practices to protect local flora and fauna.",
    ],
  },
  {
    title: "Cultural Sensitivity",
    icon: Heart,
    points: [
      "Respect Local Customs: We educate our travelers on local customs, traditions, and etiquette to promote cross-cultural understanding and respect.",
      "Support Local Economies: We partner with local businesses and communities to support economic development and fair trade practices.",
      "Community Development Projects: We support community development projects that improve the lives of local people and promote sustainable tourism practices.",
    ],
  },
  {
    title: "Travel Guidelines",
    icon: Users,
    points: [
      "Research your destination's culture, customs, and environment.",
      "Check local regulations and health guidelines.",
      "Respect local traditions and dress modestly when necessary.",
      "Keep a safe distance from wildlife and don't feed animals.",
      "Use public transport or walk/bike when possible.",
      "Support sustainable tourism practices.",
    ],
  },
  {
    title: "After Your Visit",
    icon: Recycle,
    points: [
      "Leave nothing behind: Take all your belongings with you.",
      "Share your experiences: Write reviews and share responsible travel experiences.",
      "Support sustainable tourism: Choose operators that follow sustainable practices.",
      "Promote conservation: Share knowledge about wildlife protection.",
    ],
  },
];

const planningLinks = [
  {
    name: "Travel Tips",
    icon: LightbulbIcon,
    href: "/travel-info",
    description: "Essential tips for your journey",
  },
  {
    name: "Airlines",
    icon: Plane,
    href: "/services/air-ticketing",
    description: "Book your flights",
  },
  {
    name: "Accommodation",
    icon: Building,
    href: "/services/accommodation",
    description: "Find your perfect stay",
  },
  {
    name: "Tours",
    icon: Map,
    href: "/destinations",
    description: "Explore tour options",
  },
];

export default function Ecocentric() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Ecocentric - Responsible Tourism | Ecoplace Tours</title>
        <meta
          name="description"
          content="Learn about our commitment to responsible tourism and sustainable travel practices."
        />
      </Helmet>

      <PageBanner
        title="Responsible Tourism"
        description="At Ecoplace Tours, we're committed to sustainable tourism practices that benefit both our travelers and the local communities we visit."
        backgroundImage="/5O5A9186.JPG"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <section.icon className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-4">
                {section.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-black-600 max-w-3xl mx-auto font-bold">
            By joining Ecoplace Tours, you're supporting responsible tourism
            practices that benefit both people and the planet. Thank you for
            choosing to travel with us!
          </p>
        </motion.div>

        {/* Planning Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24 py-16 bg-gray-100/100 rounded-3xl"
        >
          <h2 className="text-3xl font-bold text-center mb-16">
            Planning your trip to Uganda
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto px-4">
            {planningLinks.map((item, index) => (
              <Link key={item.name} href={item.href}>
                <motion.div
                  className="flex flex-col items-center text-center cursor-pointer group"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full shadow-lg flex items-center justify-center mb-4 group-hover:bg-primary/5 transition-colors">
                    <item.icon className="w-8 h-8 md:w-10 md:h-10 text-gray-900 stroke-[2.5]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {item.description}
                  </p>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
